import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  CheckCircle2, Circle, ChevronDown, ChevronRight, History, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { STAGE_LABEL } from '@/lib/roles';

/**
 * Iter45 — Optional Stage Quality Checks panel.
 *
 * Advisory only. Ticking / unticking NEVER blocks or changes Stage Sign-off,
 * Advance Stage, Work Orders, Trips, or reports. The panel is read-only
 * for roles not assigned to the project.
 *
 * Props:
 *   projectId  required
 *   stage      required (one of the 6 pipeline stage keys)
 *   compact    optional — used on Project card header (badge-only mode)
 */
export function QualityChecksPanel({ projectId, stage, compact = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [openHistoryFor, setOpenHistoryFor] = useState(null);

  const reload = useCallback(() => {
    if (!projectId || !stage) return;
    api.get(`/projects/${projectId}/quality-checks?stage=${stage}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || 'Unable to load quality checks'));
  }, [projectId, stage]);

  useEffect(() => { reload(); }, [reload]);

  const onTick = async (item) => {
    setBusyId(item.id);
    try {
      const note = window.prompt(`Optional note for "${item.name}" (leave blank to skip):`, '') ?? '';
      await api.post(`/projects/${projectId}/quality-checks/tick`,
                     { stage, check_id: item.id, note: note || null });
      reload();
      toast.success(`Ticked · ${item.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not tick');
    } finally { setBusyId(null); }
  };

  const onUntick = async (item) => {
    const reason = window.prompt(
      `Un-tick "${item.name}"? Please write a short reason (required):`, '');
    if (!reason || reason.trim().length < 3) {
      toast.error('A reason (min 3 chars) is required to un-tick.');
      return;
    }
    setBusyId(item.id);
    try {
      await api.post(`/projects/${projectId}/quality-checks/untick`,
                     { stage, check_id: item.id, reason: reason.trim() });
      reload();
      toast.success(`Un-ticked · ${item.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not un-tick');
    } finally { setBusyId(null); }
  };

  if (compact) {
    // Just the badge — no list. Used in card headers.
    if (!data) return null;
    return (
      <QcBadge done={data.done} total={data.total} testId={`qc-badge-${projectId}-${stage}`} />
    );
  }

  if (error) {
    return (
      <div className="border rounded-md p-3 text-sm text-[hsl(var(--danger))]"
           style={{ borderColor: 'hsl(var(--danger))', background: 'hsl(0 90% 97%)' }}
           data-testid={`qc-panel-error-${projectId}`}>{error}</div>
    );
  }

  if (!data) {
    return (
      <div className="border rounded-md p-3 text-[13px] text-[hsl(var(--foreground-muted))]"
           style={{ borderColor: 'hsl(var(--border))' }}>Loading quality checks…</div>
    );
  }

  return (
    <div className="border rounded-xl bg-[hsl(var(--card))]"
         style={{ borderColor: 'hsl(var(--border))' }}
         data-testid={`qc-panel-${projectId}-${stage}`}>
      <div className="px-4 py-3 border-b flex items-center gap-2"
           style={{ borderColor: 'hsl(var(--border))' }}>
        <Info size={14} className="text-[hsl(var(--foreground-muted))]" />
        <div className="flex-1 min-w-0">
          <div className="font-heading text-sm truncate">
            Optional quality checks · {STAGE_LABEL[stage] || stage}
          </div>
          <div className="text-[11.5px] text-[hsl(var(--foreground-muted))]">
            Advisory only — these never block Stage Sign-off or Advance Stage.
          </div>
        </div>
        <QcBadge done={data.done} total={data.total} testId={`qc-badge-${projectId}-${stage}`} />
      </div>
      <ul className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}
          data-testid={`qc-list-${projectId}-${stage}`}>
        {data.items.map((it) => (
          <QcRow key={it.id} item={it}
                 canEdit={data.can_edit}
                 busy={busyId === it.id}
                 historyOpen={openHistoryFor === it.id}
                 onToggleHistory={() => setOpenHistoryFor(openHistoryFor === it.id ? null : it.id)}
                 onTick={onTick} onUntick={onUntick} />
        ))}
      </ul>
    </div>
  );
}


function QcRow({ item, canEdit, busy, historyOpen, onToggleHistory, onTick, onUntick }) {
  const CheckIcon = item.ticked ? CheckCircle2 : Circle;
  return (
    <li className="px-4 py-2.5" data-testid={`qc-row-${item.id}`}>
      <div className="flex items-start gap-3">
        <button type="button"
                disabled={!canEdit || busy}
                onClick={() => item.ticked ? onUntick(item) : onTick(item)}
                className={`mt-0.5 flex-none transition-opacity ${(!canEdit || busy) ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-70'}`}
                title={canEdit ? (item.ticked ? 'Un-tick (reason required)' : 'Tick as done') : 'Only assigned team members can tick'}
                data-testid={`qc-toggle-${item.id}`}
                aria-label={item.ticked ? 'Un-tick' : 'Tick'}>
          <CheckIcon size={18}
                     className={item.ticked ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--foreground-muted))]'} />
        </button>
        <div className="min-w-0 flex-1">
          <div className={`text-[13.5px] font-medium ${item.ticked ? 'text-[hsl(var(--foreground))]' : ''}`}>
            {item.name}
          </div>
          <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-0.5">{item.verifies}</div>
          {item.ticked && (
            <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-1">
              <span className="text-[hsl(var(--success))]">✓ Ticked</span>{' '}
              by {item.ticked_by_email || '—'} · {item.ticked_at ? new Date(item.ticked_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
              {item.note && <> · <em>“{item.note}”</em></>}
            </div>
          )}
          {item.history && item.history.length > 0 && (
            <>
              <button type="button"
                      onClick={onToggleHistory}
                      className="text-[11px] text-[hsl(var(--foreground-muted))] mt-1 inline-flex items-center gap-0.5 hover:underline"
                      data-testid={`qc-history-toggle-${item.id}`}>
                {historyOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                <History size={10} /> {item.history.length} event{item.history.length === 1 ? '' : 's'}
              </button>
              {historyOpen && (
                <ul className="mt-1 ml-3 border-l pl-2 space-y-0.5"
                    style={{ borderColor: 'hsl(var(--border))' }}
                    data-testid={`qc-history-list-${item.id}`}>
                  {item.history.map((h, i) => (
                    <li key={i} className="text-[11px] text-[hsl(var(--foreground-muted))]">
                      <span className={h.action === 'tick' ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--warning))]'}>
                        {h.action === 'tick' ? '● ticked' : '○ un-ticked'}
                      </span>
                      {' '}by {h.by_email || '—'} · {h.at ? new Date(h.at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                      {(h.note || h.reason) && <> · <em>“{h.note || h.reason}”</em></>}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </li>
  );
}


export function QcBadge({ done, total, testId }) {
  const complete = total > 0 && done === total;
  return (
    <span className="chip text-[11px] tabular-nums inline-flex items-center gap-1"
          data-testid={testId}
          style={{
            background: complete ? 'hsl(var(--success-soft))' : 'hsl(var(--surface-2))',
            color:      complete ? 'hsl(var(--success))'      : 'hsl(var(--foreground-muted))',
            borderColor: 'hsl(var(--border))',
          }}
          title={`${done} of ${total} optional quality checks ticked for this stage. Advisory only — never blocks sign-off.`}>
      QC {done} of {total}
    </span>
  );
}
